/**
 * Centralized number formatting utilities for consistent display across all charts.
 *
 * Performance: Intl.NumberFormat instances are cached per configuration key
 * to avoid thousands of allocations when formatting chart axis labels and
 * data points (each `new Intl.NumberFormat()` is ~0.1-0.3ms).
 */

export type NumberFormatType = 'decimal' | 'integer' | 'percentage' | 'currency' | 'compact';

// ── Cached Intl.NumberFormat pool ──────────────────────────────────────────

const formatterCache = new Map<string, Intl.NumberFormat>();

function getCachedFormatter(
  locale: string,
  options: Intl.NumberFormatOptions,
): Intl.NumberFormat {
  const key = `${locale}|${JSON.stringify(options)}`;
  let fmt = formatterCache.get(key);
  if (!fmt) {
    fmt = new Intl.NumberFormat(locale, options);
    formatterCache.set(key, fmt);
  }
  return fmt;
}

// ── Cached Intl.DateTimeFormat pool ────────────────────────────────────────

const dateFormatterCache = new Map<string, Intl.DateTimeFormat>();

function getCachedDateFormatter(
  locale: string,
  options: Intl.DateTimeFormatOptions,
): Intl.DateTimeFormat {
  const key = `${locale}|${JSON.stringify(options)}`;
  let fmt = dateFormatterCache.get(key);
  if (!fmt) {
    fmt = new Intl.DateTimeFormat(locale, options);
    dateFormatterCache.set(key, fmt);
  }
  return fmt;
}

export interface NumberFormatConfig {
  type?: NumberFormatType;
  decimals?: number;
  locale?: string;
  currency?: string;
  compactDisplay?: 'short' | 'long';
}

/**
 * Format a number according to the specified configuration
 * Uses native Intl.NumberFormat for locale-aware formatting
 * 
 * @param value - The number to format
 * @param config - Formatting configuration
 * @returns Formatted string representation of the number
 * 
 * @example
 * formatNumber(12.3453245325345, { decimals: 2 }) // "12.35"
 * formatNumber(1234.56, { type: 'currency', currency: 'USD' }) // "$1,234.56"
 * formatNumber(0.1234, { type: 'percentage' }) // "12.34%"
 * formatNumber(1500000, { type: 'compact' }) // "1.5M"
 */
export const formatNumber = (
  value: number | null | undefined,
  config: NumberFormatConfig = {}
): string => {
  // Handle null, undefined, or NaN values
  if (value === null || value === undefined || isNaN(value)) {
    return '-';
  }

  const {
    type = 'decimal',
    decimals = 2,
    locale = 'en-US',
    currency = 'USD',
    compactDisplay = 'short'
  } = config;

  try {
    switch (type) {
      case 'integer':
        return getCachedFormatter(locale, {
          maximumFractionDigits: 0,
          minimumFractionDigits: 0
        }).format(value);

      case 'percentage':
        return getCachedFormatter(locale, {
          style: 'percent',
          minimumFractionDigits: decimals,
          maximumFractionDigits: decimals
        }).format(value / 100);

      case 'currency':
        return getCachedFormatter(locale, {
          style: 'currency',
          currency,
          minimumFractionDigits: decimals,
          maximumFractionDigits: decimals
        }).format(value);

      case 'compact':
        return getCachedFormatter(locale, {
          notation: 'compact',
          compactDisplay,
          maximumFractionDigits: decimals
        }).format(value);

      case 'decimal':
      default:
        return getCachedFormatter(locale, {
          minimumFractionDigits: decimals,
          maximumFractionDigits: decimals
        }).format(value);
    }
  } catch (error) {
    // Fallback to simple toFixed if Intl fails
    console.warn('Number formatting failed, using fallback', error);
    return value.toFixed(decimals);
  }
};

/**
 * Format a large number in compact notation (1.5M, 2.3K, etc.)
 * Shorthand for formatNumber with type: 'compact'
 */
export const formatCompact = (value: number | null | undefined, decimals: number = 1): string => {
  return formatNumber(value, { type: 'compact', decimals });
};

/**
 * Format a number as currency
 * Shorthand for formatNumber with type: 'currency'
 */
export const formatCurrency = (
  value: number | null | undefined,
  currency: string = 'USD',
  decimals: number = 2
): string => {
  return formatNumber(value, { type: 'currency', currency, decimals });
};

/**
 * Format a number as percentage
 * Shorthand for formatNumber with type: 'percentage'
 */
export const formatPercentage = (
  value: number | null | undefined,
  decimals: number = 2
): string => {
  return formatNumber(value, { type: 'percentage', decimals });
};

/**
 * Format a number as integer (no decimals)
 * Shorthand for formatNumber with type: 'integer'
 */
export const formatInteger = (value: number | null | undefined): string => {
  return formatNumber(value, { type: 'integer' });
};

/**
 * Default formatter for chart values.
 * Whole numbers display without decimals (10 not 10.00);
 * fractional values show up to 2 decimal places.
 */
export const formatChartValue = (value: number | null | undefined): string => {
  if (value === null || value === undefined || isNaN(value)) return '-';
  if (Number.isInteger(value)) {
    return formatNumber(value, { type: 'integer' });
  }
  return formatNumber(value, { decimals: 2 });
};

/**
 * Detect if a value looks like an epoch timestamp in milliseconds
 * Checks if it's a number in the range ~2000-01-01 to ~2100-01-01
 */
export const isEpochTimestamp = (value: any): boolean => {
  if (typeof value !== 'number') return false;
  // Epoch ms range: 2000-01-01 (946684800000) to 2100-01-01 (4102444800000)
  return value > 946684800000 && value < 4102444800000;
};

/**
 * Detect if a semantic_type represents a date/datetime
 */
export const isDateSemanticType = (semanticType: string | undefined | null): boolean => {
  if (!semanticType) return false;
  const lower = semanticType.toLowerCase();
  return lower === 'date' || lower === 'datetime' || lower === 'timestamp';
};

/**
 * Detect if a value is a date/datetime string
 * Checks for common date patterns like ISO dates, slash formats, etc.
 */
export const isDateLike = (value: any): boolean => {
  if (typeof value !== 'string') return false;
  
  // Check common date patterns
  const datePatterns = [
    /^\d{4}-\d{2}-\d{2}/, // ISO date: 2026-01-25
    /^\d{4}\/\d{2}\/\d{2}/, // Slash format: 2026/01/25
    /^\d{2}\/\d{2}\/\d{4}/, // US format: 01/25/2026
    /^\d{4}-\d{2}-\d{2}T/, // ISO datetime: 2026-01-25T21:54:00
  ];
  
  return datePatterns.some(pattern => pattern.test(value));
};

/**
 * Format a date/datetime value for display
 * 
 * @param value - Date string, timestamp, or Date object
 * @param format - Display format type
 * @returns Formatted date string
 * 
 * @example
 * formatDate('2026-01-25', 'date') // "Jan 25, 2026"
 * formatDate('2026-01-25T21:54:00', 'datetime') // "Jan 25, 2026, 9:54 PM"
 * formatDate('2026-01-25T21:54:00', 'time') // "9:54 PM"
 */
const DATE_FORMAT_OPTIONS: Record<string, Intl.DateTimeFormatOptions> = {
  date: { year: 'numeric', month: 'short', day: 'numeric' },
  datetime: { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' },
  time: { hour: '2-digit', minute: '2-digit' },
};

export const formatDate = (
  value: string | number | Date | null | undefined,
  format: 'date' | 'datetime' | 'time' = 'date'
): string => {
  if (!value) return '-';
  
  const date = new Date(value);
  
  // Check if valid date
  if (isNaN(date.getTime())) {
    return String(value);
  }
  
  try {
    const options = DATE_FORMAT_OPTIONS[format] ?? DATE_FORMAT_OPTIONS.date;
    return getCachedDateFormatter('en-US', options).format(date);
  } catch (error) {
    console.warn('Date formatting failed, using fallback', error);
    return String(value);
  }
};

/**
 * Format a byte count into a human-readable file size string.
 *
 * @param bytes - Number of bytes
 * @returns Formatted string like "1.5 KB", "3.2 MB", etc.
 *
 * @example
 * formatFileSize(512)       // "512 B"
 * formatFileSize(1536)      // "1.5 KB"
 * formatFileSize(2097152)   // "2.0 MB"
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
};

/**
 * Smart formatter that auto-detects the value type and formats accordingly
 * - Dates/datetimes → formatted date
 * - Numbers → 2 decimal places
 * - Everything else → string
 */
export const formatValue = (value: any): string => {
  if (value === null || value === undefined) {
    return '-';
  }
  
  // Check for date first
  if (isDateLike(value)) {
    return formatDate(value, 'date');
  }
  
  // Then check for number
  if (typeof value === 'number') {
    return formatChartValue(value);
  }
  
  // Default to string
  return String(value);
};
